/**
 * Editor-side helpers for the numeric answer config (SOC-5990/SOC-5991
 * introduced the storage; SOC-5993 is the first reader/writer on the
 * frontend). See NumericAnswerConfig in ~teacher/models/quizzes for the
 * shape this operates on, and
 * services/teacher/refactor/modules/quizzes/utils/questionConfig.ts for the
 * server-side write contract these helpers mirror.
 */

import {
  DEFAULT_NUMERIC_ANSWER_CONFIG,
  NumericAnswerMode,
  QuestionTypes,
  numericAnswerConfigEquals,
  writeNumericAnswerConfig,
} from '@showbie-socrative/socrative-utils';

import { NumericAnswerConfig } from '~teacher/models/quizzes';

import {
  NumericAnswerKind,
  RepeatedNumericAnswer,
  canonicalizeNumericAnswer,
  classifyNumericAnswer,
  findRepeatedNumericAnswers,
} from './numericExpression';

/** What a Numeric question opens with when nothing is stored yet. */
export const DEFAULT_EDITOR_ANSWER_CONFIG: NumericAnswerConfig = {
  version: DEFAULT_NUMERIC_ANSWER_CONFIG.version,
  mode: DEFAULT_NUMERIC_ANSWER_CONFIG.mode,
  acceptEquivalent: DEFAULT_NUMERIC_ANSWER_CONFIG.accept_equivalent,
};

/**
 * Modes the segmented control renders. Range stays in the stored enum
 * (SOC-5990) so it needs no config-shape change to enable later, but it's
 * hidden for this release (decision 2026-09-22) — it never appears here.
 */
export const VISIBLE_NUMERIC_MODES: NumericAnswerMode[] = [
  NumericAnswerMode.ExactValue,
  NumericAnswerMode.Expression,
];

/**
 * accept_equivalent as it's saved. Expression mode doesn't grade equivalence
 * yet, so an Expression question always saves it off. The editor keeps the
 * teacher's choice in state, so switching back to Exact value restores it.
 */
export function savedAcceptEquivalent(
  mode: NumericAnswerMode,
  acceptEquivalent: boolean
): boolean {
  return mode !== NumericAnswerMode.Expression && acceptEquivalent;
}

function toSnakeConfig(config: NumericAnswerConfig) {
  return {
    version: config.version,
    mode: config.mode,
    accept_equivalent: savedAcceptEquivalent(
      config.mode,
      config.acceptEquivalent
    ),
  };
}

export function isDefaultAnswerConfig(config: NumericAnswerConfig): boolean {
  return numericAnswerConfigEquals(
    toSnakeConfig(config),
    DEFAULT_NUMERIC_ANSWER_CONFIG
  );
}

/** Builds the snake_case write envelope for the question API. Never authors
 * range_bands — Range is hidden for this release. */
export function toAnswerConfigPayload(
  config: NumericAnswerConfig
): Record<string, unknown> {
  return writeNumericAnswerConfig({}, toSnakeConfig(config));
}

/**
 * What the editor opens with. A stored config with a visible mode always
 * wins. A stored Range config falls through to the same inference as no
 * stored config at all: the segmented control has no Range option to select
 * (nothing can take keyboard focus), and this editor never authors
 * range_bands, so re-saving a Range row as is would send an editor-shaped
 * Range envelope with no bands and the server would 400
 * (RangeRequiresBands). A question with no usable stored config — new,
 * saved before SOC-5990, or stored as Range — opens on the default, except
 * when it already holds an expression answer: opening that on Exact value
 * would instantly flag every row invalid, so it opens on Expression instead.
 */
export function resolveEditorAnswerConfig(
  stored: NumericAnswerConfig | null | undefined,
  answerTexts: string[]
): NumericAnswerConfig {
  if (stored && VISIBLE_NUMERIC_MODES.includes(stored.mode)) {
    return stored;
  }
  // A stored Range config still has a real acceptEquivalent choice worth
  // keeping; only the mode itself is unusable here.
  const base = stored
    ? {
        ...DEFAULT_EDITOR_ANSWER_CONFIG,
        acceptEquivalent: stored.acceptEquivalent,
      }
    : DEFAULT_EDITOR_ANSWER_CONFIG;
  const hasLegacyExpression = answerTexts.some(
    (text) => classifyNumericAnswer(text) === 'expression'
  );
  return hasLegacyExpression
    ? { ...base, mode: NumericAnswerMode.Expression }
    : base;
}

/**
 * What to put on the write payload's `answer_config` field — `undefined` to
 * omit it (leave whatever is stored untouched), `null` to reset to the
 * default, or the config to store. Never sends the default config as an
 * explicit envelope: every existing Numeric question has no stored row today,
 * and unconditionally writing one on every save would turn an ordinary edit
 * (the point value, say) into a write against quizzes_question_config for
 * every Numeric question ever saved. When a row already exists and the
 * editor is back at the default, `null` clears it rather than leaving a
 * redundant explicit-default row behind.
 */
export function resolveAnswerConfigToSend(
  questionType: QuestionTypes,
  editorConfig: NumericAnswerConfig | null | undefined,
  persistedConfig: NumericAnswerConfig | null | undefined
): NumericAnswerConfig | null | undefined {
  if (questionType !== QuestionTypes.Numeric || !editorConfig) {
    return undefined;
  }
  if (isDefaultAnswerConfig(editorConfig)) {
    return persistedConfig ? null : undefined;
  }
  return editorConfig;
}

/**
 * Whether an accepted answer's classification is rejected by the selected
 * mode — the exact_value/expression half of the server gate in
 * services/teacher/refactor/modules/quizzes/utils/numericAnswers.ts
 * (validateExactValueOrExpression). Used to flag a row inline and block save
 * when a teacher switches mode with an incompatible answer already entered,
 * rather than finding out from a server 400 on save.
 */
export function isExpressionRejectedByMode(
  kind: NumericAnswerKind,
  mode: NumericAnswerMode
): boolean {
  return mode === NumericAnswerMode.ExactValue && kind === 'expression';
}

/**
 * Every answer that repeats an earlier one under the editor's config, judged
 * on the form each answer will be saved in.
 */
export function findRepeatedAnswers(
  answerTexts: string[],
  config: NumericAnswerConfig
): RepeatedNumericAnswer[] {
  return findRepeatedNumericAnswers(
    answerTexts.map((text) => canonicalizeNumericAnswer(text)),
    toSnakeConfig(config)
  );
}
